<template>
    <div id="kiosk-home" class="kiosk-home">
        <!-- Language selector -->
        <kiosk-language-bar
            v-if="screen === 'home' || screen === 'out_of_operation'"
            :languages="enabled_languages"
        />

        <!-- Home / start screen. One button per flow the saved check-in mode
             offers — the Guest Hub setting decides both which appear and, for
             each, which reservation-search methods the guest gets next. -->
        <div v-if="screen === 'home'" class="screen-body landing">
            <!-- A scanned QR code that cannot go on (already checked in, not
                 found, ...) is answered here rather than on the hub. -->
            <div
                v-if="qr_notice"
                class="alert kiosk-qr-notice"
                :class="`alert-${qr_notice.type}`"
                role="alert"
            >
                <i
                    class="fas mr-2"
                    :class="qr_notice.type === 'info' ? 'fa-info-circle' : 'fa-exclamation-circle'"
                    aria-hidden="true"
                ></i>
                {{ qr_notice.msg }}
            </div>

            <button
                v-for="flow in landingFlows"
                :key="flow"
                type="button"
                class="kiosk-btn"
                :class="flowButtonClass(flow)"
                @click="startFlow(flow)"
            >
                <span>{{ flowLabel(flow) }}</span>
                <img
                    v-if="flow === KIOSK_FLOWS.CONFIRM_PRE_CHECKIN"
                    :src="qrIconSrc"
                    class="qr-icon"
                    alt=""
                />
            </button>

            <!-- Check-out duty has a single button: the hub behind it offers
                 every search method, so there is nothing to choose here. -->
            <button
                v-if="checkoutOpen"
                type="button"
                class="kiosk-btn kiosk-btn-primary"
                @click="startCheckout"
            >
                <span>{{ $t('virtual_kiosk.start_checkout') }}</span>
            </button>
        </div>

        <!-- Out of operating time -->
        <div v-else-if="screen === 'out_of_operation'" class="screen-body out-of-operation">
            <h2 class="oo-title">{{ $t('virtual_kiosk.out_of_operating_time') }}</h2>
            <p class="oo-datetime">{{ currentDateTime }}</p>

            <!-- Both configured windows are listed, whichever side the clock
                 last served: a guest at the closed kiosk wants to know when
                 EITHER service is next available. -->
            <div v-if="checkinConfigured" class="oo-window">
                <div class="oo-window-label">{{ $t('virtual_kiosk.checkin_time') }}</div>
                <div class="oo-window-value">
                    {{ operatingHours.checkin.start }} &ndash; {{ operatingHours.checkin.end }}
                </div>
            </div>

            <div v-if="checkoutConfigured" class="oo-window">
                <div class="oo-window-label">{{ $t('virtual_kiosk.checkout_time') }}</div>
                <div class="oo-window-value">
                    {{ operatingHours.checkout.start }} &ndash; {{ operatingHours.checkout.end }}
                </div>
            </div>

            <!-- Editable through the Admin Portal in the future -->
            <div class="oo-message">
                {{ outOfOperationMessage }}
            </div>
        </div>

        <!-- Coming back from inside the kiosk (Back, the idle auto-reset, a
             finished session). Nothing is starting, so this screen announces
             nothing of its own while it loads — the loading overlay in App.vue
             carries it, and the home screen replaces this the moment it is
             ready. -->
        <div v-else-if="screen === 'loading' && internal_return" class="screen-body" aria-hidden="true"></div>

        <!-- Now Starting / Now Closing / No Operation -->
        <div v-else class="screen-body status-screen" style="z-index: 999999; background: #fff; position: relative;">
            <kiosk-status-panel :status="statusPanel" />
        </div>

        <!-- "Prepare QR code" step, the same one the hub's QR method opens
             with: the camera never comes up on a guest who has not had a
             moment to get their code out. -->
        <b-modal
            id="kiosk-home-qr-prepare-modal"
            hide-header
            hide-footer
            centered
            @show="qr_modals_open++"
            @hidden="closeQrModal"
        >
            <div class="text-center py-3">
                <i class="fas fa-camera qr-prepare-icon" aria-hidden="true"></i>
                <p class="qr-prepare-text mt-3 mb-4">{{ $t('virtual_kiosk.prepare_qr') }}</p>
                <button type="button" class="btn btn-kiosk-primary px-5" @click="openQrScanner">
                    {{ $t('virtual_kiosk.ok') }}
                </button>
            </div>
        </b-modal>

        <!-- The pre-registered QR button's scanner, opened right here rather
             than one screen later: the code is the first and only thing that
             flow asks a guest for, so it is asked on the tap that starts it. -->
        <kiosk-qr-scanner-modal
            ref="qrScanner"
            @shown="qr_modals_open++"
            @hidden="closeQrModal"
            @decoded="onQrScanned"
        />
    </div>
</template>

<script>
import {
    KIOSK_FLOWS,
    KIOSK_MODES,
    kioskFlowsForMode,
    kioskFlowMethods,
    kioskSideConfigured,
} from './kiosk_config';
import kioskValidationMixin from './kiosk_validation_mixin';
import KioskStatusPanel from './KioskStatusPanel.vue';
import KioskQrScannerModal from './KioskQrScannerModal.vue';
import KioskLanguageBar from './KioskLanguageBar.vue';
import { stashKioskScannedQr, takeKioskInternalReturn } from './kiosk_transition';
import { kioskFlowRejection } from './kiosk_booking_rules';
import moment from 'moment-timezone';
import { applyKioskLanguage, loadKioskLanguages } from './kiosk_language';
import loader from '@/services/loader';

// How long the "Now Starting" / "Now Closing" transition screens stay up.
const TRANSITION_MS = 2500;

// How long the notice for a scanned QR code that could not go on stays up.
const QR_NOTICE_MS = 10000;

// The landing button each flow gets. Confirm Pre Check-in keeps the QR wording
// and the green treatment it had as the "Start Check-in by Pre-Registered QR
// Code" button: a QR code is still the first thing that flow asks for.
const FLOW_BUTTONS = {
    [KIOSK_FLOWS.START_CHECKIN]: {
        label: 'virtual_kiosk.start_checkin',
        variant: 'kiosk-btn-primary',
    },
    [KIOSK_FLOWS.CONFIRM_PRE_CHECKIN]: {
        label: 'virtual_kiosk.start_checkin_qr',
        variant: 'kiosk-btn-success',
    },
    [KIOSK_FLOWS.START_PRE_CHECKIN]: {
        label: 'virtual_kiosk.start_pre_checkin',
        variant: 'kiosk-btn-primary',
    },
};

export default {
    name: 'KioskHome',
    components: { KioskStatusPanel, KioskQrScannerModal, KioskLanguageBar },
    mixins: [kioskValidationMixin],
    data() {
        return {
            // 'loading' | 'starting' | 'home' | 'closing' | 'out_of_operation' | 'no_operation'
            screen: 'loading',
            // Whether the guest arrived here from inside the kiosk rather than
            // on a boot; it decides both how this load is announced and whether
            // it is announced at all. Read here rather than in mounted(): the
            // first paint happens before mounted, and reading it late would
            // flash the "Now Starting" panel for a frame at the top of exactly
            // the navigation that is not supposed to show it.
            internal_return: takeKioskInternalReturn(),
            facility: {},
            enabled_languages: [],
            transition_timer: null,
            // The side the home screen is currently drawing (KIOSK_MODES.*), or
            // null while it is not on 'home'. Compared with the active side on
            // every tick: back-to-back windows keep the target on 'home', so
            // without this the buttons would swap silently (see evaluateScreen).
            home_side: null,
            // Open QR modals (prepare + scanner). Counted rather than
            // flagged because the prepare step hands over to the scanner: the
            // one opening and the one closing overlap, and a flag would read
            // as "nobody is here" in the gap between them.
            qr_modals_open: 0,
            // A pre-registered QR code being looked up before leaving for the
            // hub; holds the screen the same way the open scanner does.
            qr_checking: false,
            // What a scan that could not go on says to the guest: { type, msg }.
            qr_notice: null,
            qr_notice_timer: null,
        };
    },
    computed: {
        // The mockup's own icon, served from public/img rather than bundled:
        // BASE_URL is what keeps it found under the production sub-path (/f/),
        // which a hard-coded /img/... would miss.
        qrIconSrc() {
            return `${process.env.BASE_URL}img/kiosk-qr-phone.png`;
        },
        // Enum for the template's per-flow branches.
        KIOSK_FLOWS() {
            return KIOSK_FLOWS;
        },
        // The side in operation right now, read off the clock and the saved
        // hours (see kioskMode in the mixin) — not off the URL, which is the
        // same for both. At most one is true at a time.
        onCheckinDuty() {
            return this.kioskMode === KIOSK_MODES.CHECKIN;
        },
        onCheckoutDuty() {
            return this.kioskMode === KIOSK_MODES.CHECKOUT;
        },
        // Whether each side is configured: switched on in the Guest Hub
        // setting AND given both ends of a window. These decide which windows
        // the closed screen lists.
        checkinConfigured() {
            return kioskSideConfigured(this.kiosk_config, KIOSK_MODES.CHECKIN);
        },
        checkoutConfigured() {
            return kioskSideConfigured(this.kiosk_config, KIOSK_MODES.CHECKOUT);
        },
        // Neither side configured means "No Operation", exactly as it would if
        // the whole hub were off. One side configured is enough to have a
        // kiosk: it is open inside that window and closed outside it.
        sideInUse() {
            return this.checkinConfigured || this.checkoutConfigured;
        },
        // The home screen only offers what is currently in operation: the
        // active side's window is open (and, by construction, configured).
        // Outside every window evaluateScreen() shows "Out of Operating Time"
        // instead, and the clock brings the screen back the moment a window
        // opens again.
        checkinOpen() {
            return this.onCheckinDuty && this.kioskCheckinInUse && this.kioskWithinCheckinHours;
        },
        checkoutOpen() {
            return this.onCheckoutDuty && this.kioskCheckoutInUse && this.kioskWithinCheckoutHours;
        },
        // The buttons on the start screen: the flows the saved check-in mode
        // offers, and nothing while the check-in window is shut. Every mode
        // but "Confirm Pre Check-in + Start Check-in" resolves to a single
        // button, which is why the hub can recover the flow on its own when a
        // guest arrives there without one (kioskAutoFlow).
        landingFlows() {
            if (!this.checkinOpen) return [];
            return kioskFlowsForMode(this.kiosk_config?.checkin_mode);
        },
        // Where the kiosk belongs right now. Recomputed on every clock tick
        // (kiosk_now) and whenever the config watch picks up a staff edit, so
        // evaluateScreen() only ever has to compare it against the current
        // screen — no reload, no staff action.
        desiredScreen() {
            if (!this.kiosk_config?.enabled || !this.sideInUse) return 'no_operation';
            return this.checkinOpen || this.checkoutOpen ? 'home' : 'out_of_operation';
        },
        operatingHours() {
            return this.kiosk_config?.operating_hours ?? {
                checkin: { start: '', end: '' },
                checkout: { start: '', end: '' },
            };
        },
        currentDateTime() {
            return this.kiosk_now.format('YYYY/M/D H:mm');
        },
        outOfOperationMessage() {
            return this.kiosk_config?.out_of_operation_message
                || this.$t('virtual_kiosk.out_of_operation_note');
        },
        // Which status the shared panel draws. 'loading' is the pre-boot state
        // of the same screen the guest is about to see start, so it shows
        // "Now Starting" rather than a fourth wording of its own.
        statusPanel() {
            if (this.screen === 'closing') return 'closing';
            if (this.screen === 'no_operation') return 'no_operation';
            return 'starting';
        },
    },
    // Before data() and the first paint, so the start screen is drawn in the
    // kiosk's language rather than repainted into it a frame later.
    beforeCreate() {
        applyKioskLanguage(this.$i18n);
    },
    async mounted() {
        // On a boot this screen is its own loading screen: `screen` starts at
        // 'loading', which draws the "Now Starting" panel before the first
        // request goes out, so the kiosk overlay would only cover it with a
        // second spinner saying the same thing — silenced for the boot, with
        // the requests still counted.
        //
        // A return is the opposite case: this screen deliberately says nothing
        // of its own while it loads, so the loader is left free to draw the
        // overlay every other page uses.
        const announces_itself = ! this.internal_return;
        if (announces_itself) loader.silence();

        try {
            this.facility = await this.$lionheart
                .post('/facility/list', { slug_name: this.$route.params.slug_name })
                .then((r) => this.catcher(r.data, 'data[0].id', (r) => r.data[0]))
                .catch((e) => this.catcher(e));
            if (this.facility.error.has_error) return this.$router.push('/404');

            await Promise.all([
                this.kioskLoadConfig(this.facility.id),
                this.loadLanguages(),
            ]);

            this.evaluateScreen({ initial: true });

            // Re-evaluate every second. This page IS the starting page, so per the
            // operating-time rule it is the only place allowed to close the kiosk:
            // guests mid-session are on other routes and are never interrupted.
            this.kioskStartClock(() => this.evaluateScreen());
        } finally {
            if (announces_itself) loader.resume();
        }
    },
    beforeDestroy() {
        if (this.transition_timer) clearTimeout(this.transition_timer);
        this.clearQrNotice();
    },
    methods: {
        evaluateScreen({ initial = false } = {}) {
            // Let a running Starting/Closing transition finish.
            if (this.transition_timer) return;

            // A guest getting their QR code out, or holding it up to the
            // camera, is mid-session, and the same rule the hub applies to its
            // own scanner holds here: the window closing does not pull the
            // screen out from under them. The clock runs on, so the moment the
            // QR modals are gone — decoded or dismissed — the next tick lands
            // wherever the kiosk belongs.
            if (this.qr_modals_open > 0 || this.qr_checking) return;

            const target = this.desiredScreen;

            if (target === 'no_operation') {
                // Nothing to announce — the Guest Hub is switched off.
                this.settle();
                return;
            }

            if (initial) {
                // `?closing=true` means we were sent here by the check-out hub
                // because the check-out window just ended. Announce it the way
                // this screen would have if the guest had been standing in
                // front of it. This one survives a return trip: it reports what
                // the kiosk just did, not how the guest got here.
                //
                // When a different side is open by now (check-out ended at
                // 11:00, check-in starts at 11:00) the guest also gets "Now
                // Starting" after it, so the hand-over reads the same as it
                // does on a screen that was never left.
                if (this.$route.query.closing === 'true') {
                    this.transitionTo('closing', target === 'home' ? ['starting'] : []);
                    return;
                }

                // A guest who pressed Back is not starting anything, and making
                // them watch "Now Starting" for two and a half seconds before
                // the screen they asked for is the kiosk talking over them.
                // Land on it.
                if (this.internal_return) {
                    this.settle();
                    return;
                }

                this.transitionTo('starting');
                return;
            }

            if (target === this.screen) {
                // Back-to-back windows: the target never leaves 'home', but the
                // side it shows has changed. Close the old side and start the
                // new one, the same two beats a window ending and another
                // opening would play, so the buttons never swap silently.
                if (target === 'home' && this.kioskMode !== this.home_side) {
                    this.transitionTo('closing', ['starting']);
                    this.refreshConfigAtBoundary();
                }
                return;
            }

            if (target === 'home') {
                // A window just opened (clock crossed the start, or staff
                // widened the hours / switched a mode on).
                this.transitionTo('starting');
                this.refreshConfigAtBoundary();
            } else if (this.screen === 'home') {
                this.transitionTo('closing');
                this.refreshConfigAtBoundary();
            } else {
                // Coming back from "No Operation" outside the window: land on
                // the closed screen directly, there is nothing to close.
                this.settle();
            }
        },

        // Land on wherever the kiosk belongs right now and remember which side
        // the home screen is drawing, so the next tick can tell a side change
        // from "nothing happened".
        settle() {
            this.screen = this.desiredScreen;
            this.home_side = this.screen === 'home' ? this.kioskMode : null;
        },

        // The tablet sits on this screen unattended for hours, so the cached
        // settings are re-read at the one moment staleness would show: an
        // operating-window edge, where the hub is opening or closing anyway.
        // That is also the only moment a request is invisible — it runs behind
        // the "Now Starting" / "Now Closing" spinner, and the transition lands
        // on whatever the refreshed config says. Two or three requests a day,
        // none of them in front of a waiting guest.
        refreshConfigAtBoundary() {
            if (!this.facility?.id) return;
            this.kioskLoadConfig(this.facility.id);
        },

        // Show the "Now Starting" / "Now Closing" spinner briefly, then land on
        // wherever the kiosk belongs by then — recomputed rather than captured,
        // so a boundary refresh that lands during the spinner is honored on the
        // first screen the guest sees instead of flipping again a second later.
        //
        // `then` queues further spinners to play first (a side change is
        // Closing then Starting). They run off the same transition_timer, so
        // evaluateScreen() keeps its hands off for the whole chain. A queued
        // "Starting" is dropped if nothing is open by the time it is due: it
        // would announce a start and land on the closed screen.
        transitionTo(via, then = []) {
            this.screen = via;
            this.transition_timer = setTimeout(() => {
                this.transition_timer = null;
                const [next, ...rest] = then;
                if (next && (next !== 'starting' || this.desiredScreen === 'home')) {
                    this.transitionTo(next, rest);
                    return;
                }
                this.settle();
            }, TRANSITION_MS);
        },

        // The row of language buttons is a component of its own (the check-out
        // screens show the same one); all this page owes it is the facility's
        // list, loaded with everything else so the start screen is painted
        // with the buttons already on it.
        async loadLanguages() {
            this.enabled_languages = await loadKioskLanguages(this.$lionheart, this.facility.id);
        },

        flowLabel(flow) {
            return this.$t(FLOW_BUTTONS[flow]?.label ?? '');
        },

        flowButtonClass(flow) {
            return FLOW_BUTTONS[flow]?.variant ?? 'kiosk-btn-primary';
        },

        // Every landing button starts the same hub; the flow it carries is what
        // decides which reservation-search methods the guest is offered there
        // and which status they leave in.
        //
        // Confirm Pre Check-in is the one flow that opens with a scan: its
        // button says "by Pre-Registered QR Code", so the tap itself asks for
        // the code — first the "Prepare QR code" step, then the camera — and
        // the hub is only loaded once there is a code for it to look up (see
        // onQrScanned). With the QR method switched off under Reservation
        // Search there is nothing to scan, and the button goes straight to the
        // hub's other methods like the rest.
        startFlow(flow) {
            this.clearQrNotice();
            if (!this.kioskCanStartCheckin()) return this.evaluateScreen();

            if (this.flowOpensWithScan(flow)) {
                this.$bvModal.show('kiosk-home-qr-prepare-modal');
                return;
            }

            this.beginNavigation();
            this.goToKioskCheckin(flow);
        },

        flowOpensWithScan(flow) {
            return flow === KIOSK_FLOWS.CONFIRM_PRE_CHECKIN
                && kioskFlowMethods(flow, this.kiosk_config?.search_methods).includes('qr');
        },

        closeQrModal() {
            this.qr_modals_open = Math.max(0, this.qr_modals_open - 1);
        },

        // "OK" on the prepare step: the guest has their code ready, so the
        // camera takes over from here.
        openQrScanner() {
            this.$bvModal.hide('kiosk-home-qr-prepare-modal');
            this.$refs.qrScanner.open();
        },

        // The scanner read a code. Whether it finds a reservation is decided on
        // the hub, which owns the lookups and the form behind them: it takes
        // the code on entry and lands on the form when it is valid, or shows
        // the error over its methods when it is not. The code crosses the page
        // load in session storage, never the URL (see kiosk_transition.js).
        //
        // The code is looked up here first, though: one that cannot go
        // anywhere (already checked in, checked out, not pre-checked-in, not
        // found) is answered on this screen, so the guest is never sent to a
        // hub that would only show them the same refusal. The hub still runs
        // its own lookup on the code it is handed — this one only decides
        // whether to leave.
        async onQrScanned(value) {
            // The window shut while the code was being read: the hub would
            // only send the guest straight back here. The clock lands this
            // screen on "Now Closing" the moment the scanner has gone.
            if (!this.kioskCanStartCheckin()) return;

            this.clearQrNotice();
            this.qr_checking = true;
            let result;
            try {
                result = await this.checkScannedQr(value);
            } finally {
                this.qr_checking = false;
            }

            if (result) return this.showQrNotice(result);

            // Checked again: the window may have shut during the lookup.
            if (!this.kioskCanStartCheckin()) return;

            stashKioskScannedQr(value);
            this.beginNavigation();
            this.goToKioskCheckin(KIOSK_FLOWS.CONFIRM_PRE_CHECKIN);
        },

        // Same lookups, same wording, as the hub's QR method (see
        // resolveQrCode there). Resolves to null when the booking can go on,
        // or `{ info }` / `{ error }` with the message to show here.
        async checkScannedQr(value) {
            const code = String(value ?? '').trim();
            if (!code) return { error: this.$t('virtual_kiosk.qr_not_detected') };

            let bookings;
            if (code.includes('|')) {
                const [email, pass] = code.split('|').map((part) => part?.trim());
                if (!email || !pass) return { error: this.$t('virtual_kiosk.qr_invalid') };

                let response = await this.lookupBookings('/v2/facility-booking/list', {
                    mode: 'pre-checkin', email, pass,
                });
                if (response.error) return response;
                if (response.bookings.length < 1) {
                    response = await this.lookupBookings('/v2/facility-booking/list', {
                        mode: 'by-pass-email-with-formlog', email, pass,
                    });
                    if (response.error) return response;
                }
                if (response.bookings.length < 1) return { error: this.$t('touch-down.error_message') };

                // One pass, one reservation: the newest match, as the hub takes.
                bookings = response.bookings.slice(0, 1);
            } else {
                const response = await this.lookupBookings('/facility-booking/list', {
                    mode: 'reservation',
                    search: code,
                    checkin_date: moment().format('YYYY-MM-DD'),
                });
                if (response.error) return response;
                bookings = response.bookings;
            }

            if (bookings.length < 1) return { error: this.$t('checkin_by_qr.error_no_reservation') };
            if (bookings.length > 1) return { error: this.$t('checkin_by_qr.error_multiple_reservation') };

            const rejection = kioskFlowRejection(bookings[0], KIOSK_FLOWS.CONFIRM_PRE_CHECKIN, 'checkin_by_qr');
            if (!rejection) return null;
            return rejection.info
                ? { info: this.$t(rejection.info) }
                : { error: this.$t(rejection.error) };
        },

        async lookupBookings(url, params) {
            const response = await this.$lionheart
                .post(url, { facility_id: this.facility.id, ...params })
                .then((r) => r.data)
                .catch((e) => this.catcher(e));

            if (response?.error?.has_error) return { error: response.error.msg };
            return { bookings: response?.data ?? [] };
        },

        // The answer to a scan that went nowhere, over the start buttons. It
        // clears itself so the next guest does not walk up to the previous
        // one's notice, and a new tap on a button clears it sooner.
        showQrNotice({ info = '', error = '' }) {
            this.clearQrNotice();
            this.qr_notice = { type: info ? 'info' : 'danger', msg: info || error };
            this.qr_notice_timer = setTimeout(() => this.clearQrNotice(), QR_NOTICE_MS);
        },

        clearQrNotice() {
            if (this.qr_notice_timer) clearTimeout(this.qr_notice_timer);
            this.qr_notice_timer = null;
            this.qr_notice = null;
        },

        // A tapped start button spans a full page load, so the guest would
        // otherwise stare at an unchanged home screen until the next document
        // paints. What answers the tap is the loading overlay in App.vue and
        // nothing more: "Now Starting" belongs to the kiosk waking up, not to a
        // button, and the screen the guest asked for is the next thing they
        // should see. The hold is deliberately never released — this document
        // is on its way out, and the loader's own safety net takes the overlay
        // down if the navigation never happens.
        beginNavigation() {
            loader.hold();

            // Nothing may move this screen again — the clock's per-second
            // re-evaluation would otherwise redraw the home buttons, or open a
            // "Now Closing" transition, while the navigation is still in
            // flight.
            this.kioskStopClock();
            if (this.transition_timer) {
                clearTimeout(this.transition_timer);
                this.transition_timer = null;
            }
        },

        // The "Start Check-out" button. Hands over to the check-out form
        // (checkout-core), which renders the check-out hub.
        startCheckout() {
            if (!this.kioskCanStartCheckout()) return this.evaluateScreen();
            this.beginNavigation();
            const { href } = this.$router.resolve({
                name: 'checkin-facility-checkout',
                params: { slug_name: this.$route.params.slug_name },
                query: { kiosk: 'true' },
            });
            window.location.href = href;
        },

        goToKioskCheckin(flow) {
            // Full navigation (same reason as the PCI hub): the check-in routes
            // reuse MainHotelForm, and a fresh page load also guarantees no
            // state from a previous guest survives (8. Privacy).
            const { href } = this.$router.resolve({
                name: 'checkin-facility-kiosk-checkin',
                params: { slug_name: this.$route.params.slug_name },
                query: { kiosk: 'true', ...(flow ? { flow } : {}) },
            });
            window.location.href = href;
        },
    },
};
</script>

<style lang="scss" scoped>
.kiosk-home {
    font-family: 'Poppins', sans-serif;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    min-height: 100dvh; /* iPadOS / Android dynamic toolbars */
    background: #fff;
    padding: 24px 24px 32px;
    user-select: none;
    -webkit-user-select: none;
    touch-action: manipulation;
}

.screen-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 24px;
    width: 100%;
    max-width: 640px;
    margin: 0 auto;
}

/* The start screen is the one place the buttons own the whole tablet: edge
   to edge (minus the page gutter) and splitting the height between them.
   The other screens keep the narrower reading column above. */
.landing {
    max-width: 1024px;
}

/* Read from a step back, like everything else on this screen. */
.kiosk-qr-notice {
    width: 100%;
    flex: 0 0 auto;
    margin-bottom: 0;
    font-size: clamp(16px, 2.8vw, 24px);
    line-height: 1.4;
}

.kiosk-btn {
    width: 100%;
    flex: 1 1 0;
    min-height: 200px;
    max-height: 44vh;
    border: none;
    border-radius: 2px;
    color: #fff;
    font-weight: 700;
    font-size: clamp(32px, 11.5vw, 100px);
    line-height: 1.25;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: clamp(16px, 3vh, 32px);
    padding: 24px;
    cursor: pointer;
    touch-action: manipulation;

    /* Labels carry their own line breaks (\n in the locale files) so each
       language wraps where the mockups do instead of wherever the width lands. */
    span {
        text-align: center;
        white-space: pre-line;
    }

    &:active {
        filter: brightness(0.9);
    }
}

.kiosk-btn-primary {
    background: #1565d8;
}

/* "Prepare QR code" step — same treatment as the hub's copy of it. */
.qr-prepare-icon {
    font-size: 56px;
}

.qr-prepare-text {
    font-size: 18px;
    font-weight: 600;
}

.btn-kiosk-primary {
    color: #fff;
    background-color: #1565d8;
    min-height: 48px;
    font-weight: 600;
    touch-action: manipulation;

    &:hover,
    &:active {
        color: #fff;
        background-color: #0f4fae;
    }
}

.kiosk-btn-success {
    background: #00a650;
    font-size: clamp(20px, 5.4vw, 48px);

    // The mockup places the asset as drawn — dark ink on the green — so it is
    // left in its own colors rather than knocked out to white.
    .qr-icon {
        height: clamp(56px, 13vw, 120px);
        width: auto;
    }
}

/* Out of operating time */
.out-of-operation {
    justify-content: flex-start;
    padding-top: 8vh;
    text-align: center;

    .oo-title {
        display: inline-block;
        background: #1565d8;
        color: #fff;
        font-weight: 700;
        font-size: clamp(22px, 4vw, 34px);
        padding: 4px 12px;
    }

    .oo-datetime {
        color: #9e9e9e;
        font-size: clamp(18px, 3vw, 26px);
        font-weight: 600;
        margin: 0;
    }

    .oo-window {
        .oo-window-label,
        .oo-window-value {
            font-size: clamp(18px, 3vw, 26px);
            font-weight: 700;
            color: #000;
        }
    }

    .oo-message {
        border: 2px solid #c00;
        padding: 16px;
        font-weight: 700;
        font-size: clamp(14px, 2.4vw, 20px);
        max-width: 480px;
    }
}

/* Now Starting / Now Closing / No Operation. The panel itself lives in
   KioskStatusPanel.vue. */
.status-screen {
    gap: 8px;
}
</style>
